import {
  faCircleCheck,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMediaQuery, useTheme } from '@mui/material';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { isOtpActive } from '!core/redux/dbConfigurations';
import {
  TrlFeedback,
  TrlNbsp,
  TrlScreenSummary,
  TrlTextWithLink,
} from '»components/atoms';
import { Button } from '»components/atoms/Button';
import { TrlTextField } from '»components/atoms/Inputs';
import { Otp } from '»components/Auth';
import { signUp as cryptoSignUp } from '»utils/crypto';
import { useHandleSagaError } from '»utils/customHooks';

import { changePasswordServices } from '../TrlChangePassword';
import { activateUserServices } from './activateUser.services';
import {
  ActivateUserContainer,
  BoxStyled,
  TypographySubtitleStyled,
  TypographyTextStyled,
  TypographyTitleStyled,
} from './TrlActivateUser.styles';
import { trlActivateUserValidation } from './trlActivateUser.yup';

/**
 * Component for password change form with rule checklist.
 */
export const TrlActivateUser = ({ token }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [passwordPolicies, setPasswordPolicies] = useState([]);
  const [feedbackMsg, setFeedbackMsg] = useState(null);
  const [submittedWithSuccess, setSubmittedWithSuccess] = useState(false);
  const [validationCode, setValidationCode] = useState('');
  const [passwordNova, setPasswordNova] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const useOtp = useSelector((state) =>
    isOtpActive(state, 'ENFORCE_OTP_RECOVER_PASSWORD'),
  );

  const { handleSagaError } = useHandleSagaError();

  const validOTP = useOtp ? validationCode !== '' : true;

  const handleValueChange = (event) => {
    setValidationCode(event.target.value);
  };

  const handleGetOptError = (event) => {
    setFeedbackMsg(event);
  };

  /**
   * Fetches the password policies from the service and updates the state.
   */
  const fetchPasswordPolicies = () => {
    changePasswordServices.getPasswordPolicies().then((resp) => {
      setPasswordPolicies(
        resp.filter((item) => {
          return item.value !== null;
        }),
      );
    });
  };

  useEffect(() => {
    fetchPasswordPolicies();
  }, []);

  /**
   * Event handler for the new password field change.
   * @param {object} event - The change event object.
   */
  const handlePasswordNovaChange = (event) => {
    setPasswordNova(event.target.value);
  };

  /**
   * Checks if all password rules are valid.
   * @returns {boolean} - True if all rules are valid, false otherwise.
   */
  const areAllRulesValid = () => {
    return passwordPolicies.every((record) => {
      return isRuleValid(record);
    });
  };

  /**
   * Checks if a specific password rule is valid.
   * @param {object} record - The password policy record.
   * @returns {boolean} - True if the rule is valid, false otherwise.
   */
  const isRuleValid = (record) => {
    let isValid = false;

    if (record.value?.startsWith('REGEXP=')) {
      const regex = new RegExp(record.value.replace('REGEXP=', ''));
      isValid = regex.test(passwordNova);
    }

    return isValid;
  };

  /**
   * Renders the icons for the password rules.
   * @returns {JSX.Element[]} - The array of icon elements.
   */
  const renderRuleIcons = () => {
    return passwordPolicies.map((record) => {
      const isValid = isRuleValid(record);

      return (
        <div style={{ display: 'flex', paddingBottom: 12 }} key={record.id}>
          <FontAwesomeIcon
            icon={isValid ? faCircleCheck : faCircleXmark}
            color={isValid ? '#06888f' : '#dc4040'}
            style={{ paddingRight: '8px' }}
          />
          <TypographyTextStyled>{record.descr}</TypographyTextStyled>
        </div>
      );
    });
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);
    const { salt, hashPassword } = await cryptoSignUp(passwordNova);

    activateUserServices
      .updateUserByToken({
        token,
        salt,
        hashPassword,
        otp: validationCode,
      })
      .then(() => {
        setSubmittedWithSuccess(true);
        setFeedbackMsg('TRLT_UI-NEW_PW_CREATED');
        setSubmitting(false);
        window.location.href =
          document.location.origin + document.location.pathname;
      })
      .catch((error) => {
        handleSagaError(error);
        const [{ detail }] = error;
        setFeedbackMsg(detail);
        setSubmitting(false);
        setIsLoading(false);
      });
  };

  return (
    <ActivateUserContainer>
      <TrlScreenSummary title="TRLT_UI-NEW_PASSWORD">
        <>
          {feedbackMsg && (
            <>
              <TrlFeedback
                title={
                  submittedWithSuccess
                    ? 'TRLT_UI-SUCESS'
                    : 'TRLT_UI-PASSWORD_ERRORS'
                }
                msg={feedbackMsg}
                success={submittedWithSuccess}
              />
              <TrlNbsp />
            </>
          )}
          <TrlTextWithLink mainText="TRLT_UI-PASSWORD_WARNING" />
        </>
      </TrlScreenSummary>
      <div
        style={{
          display: 'flex',
          flexDirection: useMediaQuery(theme.breakpoints.down('lg'))
            ? 'column-reverse'
            : 'row',
          gap: isMobile ? '16px' : '32px',
        }}
      >
        <div>
          <Formik
            initialValues={{
              passwordNova,
              passwordConfirmar: '',
            }}
            onSubmit={handleSubmit}
            validationSchema={trlActivateUserValidation()}
            enableReinitialize
          >
            {({ isSubmitting, isValid, dirty }) => (
              <Form>
                <TrlTextField
                  name="passwordNova"
                  type="password"
                  label={<FormattedMessage id="TRLT_UI-PASSWORD" />}
                  id="passwordNova"
                  value={passwordNova}
                  autoComplete="current-password"
                  margin="normal"
                  additionalFunc={handlePasswordNovaChange}
                />
                <TrlTextField
                  name="passwordConfirmar"
                  type="password"
                  label={<FormattedMessage id="TRLT_UI-CONFIRM_PASSWORD" />}
                  id="passwordConfirmar"
                  autoComplete="current-password"
                  margin="normal"
                />
                {useOtp && (
                  <Otp
                    margin="16px 0 0 0"
                    value={validationCode}
                    onChangeValue={handleValueChange}
                    onError={handleGetOptError}
                    token={token}
                  />
                )}
                <Button
                  textId="TRLT_UI-CONFIRM"
                  type="submit"
                  id="submitAlterar"
                  sx={{
                    width: '100%',
                    marginTop: '16px',
                    marginBottom: isMobile && '16px',
                  }}
                  isLoading={isLoading}
                  disabled={
                    isSubmitting ||
                    isLoading ||
                    !(isValid && dirty && validOTP) ||
                    !areAllRulesValid()
                  }
                />
              </Form>
            )}
          </Formik>
        </div>
        <BoxStyled>
          {!isMobile && (
            <>
              <TypographyTitleStyled>
                <FormattedMessage id="TRLT_UI-PW_REQUIREMENTS" />
              </TypographyTitleStyled>
              <TypographySubtitleStyled>
                <FormattedMessage id="TRLT_UI-PW_CONDITION" />
              </TypographySubtitleStyled>
            </>
          )}
          {renderRuleIcons()}
        </BoxStyled>
      </div>
    </ActivateUserContainer>
  );
};

TrlActivateUser.propTypes = {
  token: PropTypes.string,
};
